/**
 * /dev/social — Social export studio. NOT linked from the app; local only.
 *
 * A custom, big-type, screenshot-friendly representation of a spot's
 * forecast, tuned for Instagram 4:5 carousels (1080×1350). Two card
 * variants per spot, matching the Figma "V10 — Social" frames:
 *   • forecast — huge speed/direction, single-day bars with the locked
 *                hour marked by a day/time label + connector line
 *   • map      — big basemap + compass ring, wind summary line
 *
 * The controls above the canvas let you explore the forecast to find the
 * good window; "Screenshot mode" hides everything but the canvas so you
 * (or the preview screenshot tool) capture a clean 1080×1350 frame.
 * Press Escape to leave screenshot mode.
 */
import type { Map as MaplibreMap } from "maplibre-gl";
import { type Ref, useEffect, useMemo, useRef, useState } from "react";
import { DirArrow } from "../components/Arrow";
import { CompassRing } from "../components/map/CompassRing";
import { SpotDetailMap } from "../components/map/SpotDetailMap";
import { useForecast } from "../lib/api";
import { degreesToCompass } from "../lib/scorer";
import type { EnrichedSpot, ScoredDay, ScoredHour } from "../lib/types";
import {
  barHeightPx,
  comparePeakKey,
  dayName,
  dayNum,
  dirLevelColor,
  fmtSpeed,
  knotsColor,
  peakHourKey,
  UNIT_LABELS,
} from "../lib/utils";
import styles from "./SocialExportPage.module.css";

// Marquee spots surfaced as quick-picks (only those present in the data
// show up). Known, popular launches — the ones worth leading a post with.
const MARQUEE_IDS = [
  "toro_stenstrand",
  "sandhammaren",
  "apelviken",
  "faro_sudersand",
  "oland_boda",
  "hollviken",
  "kaseberga",
  "tylosand",
];

type CardKind = "forecast" | "map";

const FULL_DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function ordinal(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return "th";
  switch (n % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

/** "Wednesday 28th - 13.00" from an ISO datetime ("YYYY-MM-DDTHH:MM…"). */
function dayTimeLabel(iso: string): string {
  const date = iso.slice(0, 10);
  // Noon avoids any DST/timezone date-rollover when reading the weekday.
  const weekday = FULL_DAYS[new Date(`${date}T12:00:00`).getDay()] ?? "";
  const n = Number.parseInt(date.slice(8, 10), 10);
  const time = iso.slice(11, 16).replace(":", ".");
  return `${weekday} ${n}${ordinal(n)} - ${time}`;
}

function hourOfDay(time: string): number {
  return Number.parseInt(time.slice(11, 13), 10);
}

// html-to-image's default font embedding crawls *every* stylesheet on the
// page to discover @font-face rules — in Vite dev that's ~40 CSS modules,
// each re-fetched, which drags a single export out to ~a minute. The canvas
// only uses one face (Inter / InterVariable.ttf), so we build its @font-face
// data-URL once and hand it to toPng via `fontEmbedCSS`, which makes it skip
// the crawl entirely. Cached across exports (the font never changes).
let fontEmbedCssPromise: Promise<string> | null = null;
function interFontEmbedCss(): Promise<string> {
  if (!fontEmbedCssPromise) {
    fontEmbedCssPromise = fetch("/fonts/InterVariable.ttf")
      .then((r) => r.blob())
      .then(
        (blob) =>
          new Promise<string>((resolve, reject) => {
            const fr = new FileReader();
            fr.onload = () => resolve(fr.result as string);
            fr.onerror = () => reject(fr.error);
            fr.readAsDataURL(blob);
          }),
      )
      .then(
        (dataUrl) =>
          `@font-face{font-family:"Inter";src:url(${dataUrl}) format("truetype");font-weight:100 900;font-style:normal;}`,
      );
  }
  return fontEmbedCssPromise;
}

/** True once a WebGL canvas has painted something other than a flat fill —
 *  used to gate the map export until the basemap is actually on-screen (the
 *  preserved drawing buffer reads back blank if we snapshot too early).
 *  Samples a downscaled 8×8 copy and checks for per-channel spread. */
function canvasHasContent(canvasEl: HTMLCanvasElement): boolean {
  const t = document.createElement("canvas");
  t.width = 8;
  t.height = 8;
  const ctx = t.getContext("2d", { willReadFrequently: true });
  if (!ctx) return true;
  try {
    ctx.drawImage(canvasEl, 0, 0, 8, 8);
    const { data } = ctx.getImageData(0, 0, 8, 8);
    let minR = 255;
    let maxR = 0;
    let minG = 255;
    let maxG = 0;
    let minB = 255;
    let maxB = 0;
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i] ?? 0;
      const g = data[i + 1] ?? 0;
      const b = data[i + 2] ?? 0;
      if (r < minR) minR = r;
      if (r > maxR) maxR = r;
      if (g < minG) minG = g;
      if (g > maxG) maxG = g;
      if (b < minB) minB = b;
      if (b > maxB) maxB = b;
    }
    return maxR - minR + (maxG - minG) + (maxB - minB) > 12;
  } catch {
    // Cross-origin taint would throw on getImageData — assume painted.
    return true;
  }
}

/** A day with at least one hour of forecast data. */
function hasHours(day: ScoredDay): day is ScoredDay & { hours: ScoredHour[] } {
  return day.hours.length > 0;
}

/** Index of the spot's best forecast day (highest day score, peak-speed
 *  tiebreak). Falls back to the first day with hours. */
function bestDayOffset(spot: EnrichedSpot): number {
  let bestOffset = -1;
  let bestKey: ReturnType<typeof peakHourKey> | null = null;
  spot.days.forEach((day, i) => {
    if (!hasHours(day)) return;
    const key = peakHourKey(spot, i);
    if (bestKey === null || comparePeakKey(key, bestKey) < 0) {
      bestKey = key;
      bestOffset = day.dayOffset;
    }
  });
  if (bestOffset >= 0) return bestOffset;
  return spot.days.find(hasHours)?.dayOffset ?? 0;
}

/** Best hour within a day: daytime (08–19), highest score, gust then time
 *  as tiebreaks — mirrors the carousel picker so the "interesting" window
 *  is selected, not the dead-calm overnight hour. Returns array index. */
function bestHourIdx(hours: ScoredHour[]): number {
  const ranked = hours
    .map((h, i) => ({ h, i }))
    .filter(({ h }) => hourOfDay(h.time) >= 8 && hourOfDay(h.time) <= 19);
  const pool = ranked.length > 0 ? ranked : hours.map((h, i) => ({ h, i }));
  pool.sort(
    (a, b) => b.h.score - a.h.score || b.h.gustMs - a.h.gustMs || a.h.time.localeCompare(b.h.time),
  );
  return pool[0]?.i ?? 0;
}

export function SocialExportPage() {
  const forecast = useForecast();
  const spots = useMemo(
    () =>
      [...(forecast.data?.spots ?? [])].sort(
        (a, b) => a.priority - b.priority || a.name.localeCompare(b.name),
      ),
    [forecast.data],
  );

  const [spotId, setSpotId] = useState<string | null>(null);
  const [dayOffset, setDayOffset] = useState(0);
  const [hourIdx, setHourIdx] = useState(0);
  const [card, setCard] = useState<CardKind>("forecast");
  const [capture, setCapture] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [filter, setFilter] = useState("");

  // The live 1080×1350 canvas node, rasterised straight to PNG on export.
  const exportRef = useRef<HTMLDivElement>(null);
  // The map card's MapLibre instance — used to wait for a painted frame
  // before snapshotting the WebGL canvas on export.
  const mapInstanceRef = useRef<MaplibreMap | null>(null);

  const spot = useMemo(
    () => spots.find((s) => s.id === spotId) ?? spots[0] ?? null,
    [spots, spotId],
  );

  // Default the selection to the spot's best day + that day's best hour
  // whenever the chosen spot changes.
  useEffect(() => {
    if (!spot) return;
    const d = bestDayOffset(spot);
    const day = spot.days.find((x) => x.dayOffset === d);
    setDayOffset(d);
    setHourIdx(day && hasHours(day) ? bestHourIdx(day.hours) : 0);
  }, [spot]);

  // Escape leaves screenshot mode.
  useEffect(() => {
    if (!capture) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setCapture(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [capture]);

  if (!spot) {
    return (
      <div className={styles.studio}>
        <p className={styles.hint}>
          {forecast.isLoading ? "Loading forecast…" : "No spots available."}
        </p>
      </div>
    );
  }

  const day = spot.days.find((d) => d.dayOffset === dayOffset);
  const hours = day && hasHours(day) ? day.hours : [];
  const heroHour = hours[hourIdx] ?? hours[0] ?? null;

  // Rasterise the live canvas node to a 1080×1350 PNG (2× for crispness) and
  // trigger a download — no manual screenshot needed. html-to-image is loaded
  // lazily so it never lands in the main app bundle.
  //
  // The map card is the awkward one. html-to-image can't be pointed at a live
  // WebGL <canvas>: it clones one by routing the pixels through an internal
  // image decode that never resolves in some engines (no catch, no timeout),
  // so an in-place export hangs forever; and embedding the map raster as a
  // multi-MB data-URL inside the SVG re-triggers the same flaky decode on the
  // final rasterise. So we composite the map ourselves: snapshot the (preserved)
  // WebGL buffer straight onto the output 2D canvas with drawImage, run html-to-
  // image only over the *chrome* (canvases removed → a light, fast SVG with a
  // transparent map hole), then paint that chrome on top. The forecast card has
  // no canvas, so it skips all of this and just rasterises its chrome SVG.
  async function exportPng() {
    const node = exportRef.current;
    if (!node || !spot) return;
    setExporting(true);

    const OUT_W = 2160;
    const OUT_H = 2700;
    const out = document.createElement("canvas");
    out.width = OUT_W;
    out.height = OUT_H;
    // Logical→output scale (the canvas is 1080×1350; preview renders it at
    // 0.5×, capture mode at 1×, but the design space is always 1080 wide).
    const nodeRect = node.getBoundingClientRect();
    const previewScale = nodeRect.width / 1080;
    const outScale = OUT_W / 1080;

    const restore: Array<() => void> = [];
    try {
      const outCtx = out.getContext("2d");
      if (!outCtx) throw new Error("no 2d context");

      // The card's surface fill. We paint this onto the output first, then
      // render the chrome SVG with a *transparent* root background (below), so
      // the map frame becomes a genuine see-through hole over the map snapshot.
      // (The card's only opaque backdrop is this root surface; with it removed
      // from the SVG, the frame's interior is transparent and the map shows
      // through instead of being painted over.)
      const surfaceBg = getComputedStyle(node).backgroundColor || "#000";

      // ── Map card: wait for a painted basemap, then snapshot it ───────────
      const map = mapInstanceRef.current;
      const mapCanvases = card === "map" ? Array.from(node.querySelectorAll("canvas")) : [];
      let mapShot: {
        canvas: HTMLCanvasElement;
        x: number;
        y: number;
        w: number;
        h: number;
      } | null = null;
      if (card === "map" && map && mapCanvases.length > 0) {
        const baseCanvas = node.querySelector<HTMLCanvasElement>(".maplibregl-canvas");
        // Wait for the basemap to actually land in the (preserved) drawing
        // buffer before snapshotting it — composite too early and the map
        // reads back blank (we've shipped blank exports doing exactly that).
        // Two stages:
        //   1. Poll until the style is loaded and every tile for the current
        //      view is in. The particle layer is a separate 2D overlay canvas,
        //      not a MapLibre source, so it never keeps the map from settling.
        //   2. Force one confirmed paint: triggerRepaint, resolve on the next
        //      'render' event (so a full frame is guaranteed drawn into the
        //      buffer), and verify the canvas holds real, non-uniform pixels —
        //      retrying until it does, since satellite tiles can lag a beat
        //      behind areTilesLoaded(). A timeout caps either stage so a stuck
        //      load never wedges the export.
        const start = performance.now();
        const elapsed = () => performance.now() - start;
        await new Promise<void>((resolve) => {
          const poll = () => {
            if ((map.loaded() && map.areTilesLoaded()) || elapsed() > 12000) resolve();
            else setTimeout(poll, 100);
          };
          poll();
        });
        await new Promise<void>((resolve) => {
          const paintOnce = () => {
            map.once("render", () => {
              const painted = baseCanvas ? canvasHasContent(baseCanvas) : true;
              if (painted || elapsed() > 12000) resolve();
              else setTimeout(paintOnce, 120);
            });
            map.triggerRepaint();
          };
          paintOnce();
        });

        // Composite basemap + particle canvases (in DOM order) into one
        // snapshot at their native pixel resolution.
        const composite = document.createElement("canvas");
        composite.width = mapCanvases[0]?.width ?? 1;
        composite.height = mapCanvases[0]?.height ?? 1;
        const compCtx = composite.getContext("2d");
        if (compCtx) {
          for (const c of mapCanvases) {
            compCtx.drawImage(c, 0, 0, composite.width, composite.height);
          }
        }

        // Where the map frame sits within the design canvas (logical px).
        const frameEl = node.querySelector<HTMLElement>(`.${styles.mapFrame}`);
        if (frameEl) {
          const fr = frameEl.getBoundingClientRect();
          mapShot = {
            canvas: composite,
            x: ((fr.left - nodeRect.left) / previewScale) * outScale,
            y: ((fr.top - nodeRect.top) / previewScale) * outScale,
            w: (fr.width / previewScale) * outScale,
            h: (fr.height / previewScale) * outScale,
          };
        }
      }

      // ── Chrome SVG prep ──────────────────────────────────────────────────
      // Hide MapLibre's control chrome (attribution/logo — unwanted, and its
      // remote logo would be fetched-and-inlined), and replace each live
      // <canvas> with an empty placeholder so html-to-image neither hangs on
      // it nor bloats the SVG with the map raster. The map shows through the
      // resulting transparent hole, painted from `mapShot` below.
      for (const el of node.querySelectorAll<HTMLElement>(".maplibregl-control-container")) {
        const prev = el.style.display;
        el.style.display = "none";
        restore.push(() => {
          el.style.display = prev;
        });
      }
      for (const canvasEl of mapCanvases) {
        const ph = document.createElement("div");
        canvasEl.replaceWith(ph);
        restore.push(() => ph.replaceWith(canvasEl));
      }
      // html-to-image carries class-based styling into the clone for HTML
      // elements but not for SVG <text>, so the compass's degree/letter labels
      // (which take their fill + font-size from a CSS module class) would fall
      // back to black at the default — i.e. larger — font size. Inline the
      // resolved text styling onto each <text> first so it survives the clone.
      for (const textEl of Array.from(node.querySelectorAll<SVGTextElement>("text"))) {
        const cs = getComputedStyle(textEl);
        const prevStyle = textEl.getAttribute("style");
        textEl.style.fill = cs.fill;
        textEl.style.fontSize = cs.fontSize;
        textEl.style.fontFamily = cs.fontFamily;
        textEl.style.fontWeight = cs.fontWeight;
        textEl.style.letterSpacing = cs.letterSpacing;
        restore.push(() => {
          if (prevStyle === null) textEl.removeAttribute("style");
          else textEl.setAttribute("style", prevStyle);
        });
      }

      const { toSvg } = await import("html-to-image");
      const fontEmbedCSS = await interFontEmbedCss();
      // With the live canvases removed, this is a light chrome-only SVG (no
      // multi-MB map raster), so its `data:` URL decodes fast and reliably.
      // We keep the `data:` URL rather than a Blob URL on purpose: an SVG that
      // contains a <foreignObject> taints the output canvas when loaded from a
      // Blob URL (so toDataURL would throw), but not from a same-origin `data:`
      // URL. crossOrigin mirrors html-to-image's own rasteriser.
      const svgDataUrl = await toSvg(node, {
        width: 1080,
        height: 1350,
        fontEmbedCSS,
        style: {
          // Strip the studio's 0.5× preview transform from the clone so the
          // export renders at full pixel size.
          transform: "none",
          transformOrigin: "top left",
          // Transparent root so the map frame is a see-through hole (see
          // surfaceBg note above). The surface is painted onto the output
          // canvas first; everything else in the chrome carries its own colour.
          background: "transparent",
        },
      });

      // Paint order, bottom→top: surface fill, then the map snapshot (clipped
      // to the frame's rounded corners), then the chrome on top — so the
      // compass overlay, frame border and summary text all land above the map,
      // while the header/divider/margins show the surface fill through the
      // chrome's now-transparent root.
      outCtx.fillStyle = surfaceBg;
      outCtx.fillRect(0, 0, OUT_W, OUT_H);
      if (mapShot) {
        const frameEl = node.querySelector<HTMLElement>(`.${styles.mapFrame}`);
        const radius =
          (frameEl ? Number.parseFloat(getComputedStyle(frameEl).borderTopLeftRadius) || 0 : 0) *
          outScale;
        outCtx.save();
        outCtx.beginPath();
        outCtx.roundRect(mapShot.x, mapShot.y, mapShot.w, mapShot.h, radius);
        outCtx.clip();
        outCtx.drawImage(mapShot.canvas, mapShot.x, mapShot.y, mapShot.w, mapShot.h);
        outCtx.restore();
      }

      await new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          outCtx.drawImage(img, 0, 0, OUT_W, OUT_H);
          resolve();
        };
        img.onerror = () => reject(new Error("chrome raster failed"));
        img.src = svgDataUrl;
      });

      const stamp = heroHour ? heroHour.time.slice(0, 16).replace(/[:T]/g, "") : "";
      const link = document.createElement("a");
      link.download = `bigwind-${spot.id}-${card}${stamp ? `-${stamp}` : ""}.png`;
      link.href = out.toDataURL("image/png");
      link.click();
    } finally {
      // Undo the prep (controls + canvas placeholders), most-recent first.
      for (const fn of restore.reverse()) fn();
      setExporting(false);
    }
  }

  const canvas =
    card === "forecast" ? (
      <ForecastCanvas
        rootRef={exportRef}
        spot={spot}
        hours={hours}
        hourIdx={hourIdx}
        heroHour={heroHour}
        onSelectHour={setHourIdx}
        interactive={!capture}
      />
    ) : (
      <MapCanvas
        rootRef={exportRef}
        spot={spot}
        heroHour={heroHour}
        onMapReady={(m) => {
          mapInstanceRef.current = m;
        }}
      />
    );

  if (capture) {
    return <div className={styles.capture}>{canvas}</div>;
  }

  const filtered = filter
    ? spots.filter((s) =>
        `${s.name} ${s.area} ${s.region}`.toLowerCase().includes(filter.toLowerCase()),
      )
    : spots;
  const marquee = MARQUEE_IDS.map((id) => spots.find((s) => s.id === id)).filter(
    (s): s is EnrichedSpot => Boolean(s),
  );

  return (
    <div className={styles.studio}>
      <div className={styles.controls}>
        <h1 style={{ fontFamily: "Inter Display", fontWeight: 800, fontSize: 28 }}>
          Social export studio
        </h1>
        <p className={styles.hint}>
          /dev/social — pick a spot, day and hour, then hit Screenshot mode for a clean 1080×1350
          frame. Escape exits. Not linked from the app.
        </p>

        {/* Card switch */}
        <div className={styles.controlRow}>
          <span className={styles.controlLabel}>Card</span>
          {(["forecast", "map"] as CardKind[]).map((k) => (
            <button
              type="button"
              key={k}
              className={`${styles.pick} ${card === k ? styles.pickActive : ""}`}
              onClick={() => setCard(k)}
            >
              {k}
            </button>
          ))}
          <button
            type="button"
            className={styles.pick}
            onClick={() => setCapture(true)}
            style={{ marginLeft: "auto" }}
          >
            Screenshot mode
          </button>
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={exportPng}
            disabled={exporting}
          >
            {exporting ? "Exporting…" : "Export PNG"}
          </button>
        </div>

        {/* Marquee quick-picks */}
        {marquee.length > 0 && (
          <div className={styles.controlRow}>
            <span className={styles.controlLabel}>Popular</span>
            {marquee.map((s) => (
              <button
                type="button"
                key={s.id}
                className={`${styles.pick} ${spot.id === s.id ? styles.pickActive : ""}`}
                onClick={() => setSpotId(s.id)}
              >
                {s.name}
              </button>
            ))}
          </div>
        )}

        {/* Full spot list (filterable) */}
        <div className={styles.controlRow}>
          <span className={styles.controlLabel}>Spot</span>
          <input
            className={styles.filterInput}
            placeholder="Filter spots…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        <div className={styles.spotList}>
          {filtered.map((s) => (
            <button
              type="button"
              key={s.id}
              className={`${styles.pick} ${spot.id === s.id ? styles.pickActive : ""}`}
              onClick={() => setSpotId(s.id)}
            >
              {s.name}
            </button>
          ))}
        </div>

        {/* Day */}
        <div className={styles.controlRow}>
          <span className={styles.controlLabel}>Day</span>
          {spot.days.filter(hasHours).map((d) => (
            <button
              type="button"
              key={d.dayOffset}
              className={`${styles.pick} ${d.dayOffset === dayOffset ? styles.pickActive : ""}`}
              onClick={() => {
                setDayOffset(d.dayOffset);
                setHourIdx(bestHourIdx(d.hours));
              }}
            >
              {d.dayOffset === 0
                ? "Today"
                : d.dayOffset === 1
                  ? "Tomorrow"
                  : `${dayName(d.date)} ${dayNum(d.date)}`}
            </button>
          ))}
        </div>

        {/* Hour */}
        <div className={styles.controlRow}>
          <span className={styles.controlLabel}>Hour</span>
          {hours.map((h, i) => (
            <button
              type="button"
              key={h.time}
              className={`${styles.pick} ${i === hourIdx ? styles.pickActive : ""}`}
              onClick={() => setHourIdx(i)}
            >
              {h.time.slice(11, 16)}
            </button>
          ))}
        </div>
      </div>

      {/* Scaled-down live preview of the exact canvas */}
      <div className={styles.previewWrap}>
        <div className={styles.previewScale}>{canvas}</div>
      </div>
    </div>
  );
}

// ── Shared header (area + name + "Best in" tags) ────────────────────────────

function CardHeader({ spot }: { spot: EnrichedSpot }) {
  return (
    <div className={styles.header}>
      <div>
        <div className={styles.area}>
          {spot.area}
          {spot.waterBody ? ` · ${spot.waterBody}` : ""}
        </div>
        <h1 className={styles.name}>{spot.name}</h1>
      </div>
      {spot.ideal.dirs.length > 0 && (
        <div className={styles.bestIn}>
          <span className={styles.bestInLabel}>Best in</span>
          <div className={styles.bestInTags}>
            {spot.ideal.dirs.map((d) => (
              <span key={d} className={styles.tag}>
                {d}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Forecast canvas ────────────────────────────────────────────────────────

interface ForecastCanvasProps {
  rootRef: Ref<HTMLDivElement>;
  spot: EnrichedSpot;
  hours: ScoredHour[];
  hourIdx: number;
  heroHour: ScoredHour | null;
  onSelectHour: (idx: number) => void;
  interactive: boolean;
}

function ForecastCanvas({
  rootRef,
  spot,
  hours,
  hourIdx,
  heroHour,
  onSelectHour,
  interactive,
}: ForecastCanvasProps) {
  const unit = "kn" as const;
  const dirLabel = heroHour ? degreesToCompass(heroHour.dirDeg) : "—";
  const accent =
    heroHour && heroHour.speedMs * 1.944 >= 3
      ? dirLevelColor(heroHour.dirDeg, spot.ideal.dirs)
      : "var(--foreground-muted)";

  // Normalise bar heights to the day's peak gust so light-wind days still
  // fill the chart vertically (it's a single-day card — cross-day scale
  // comparison isn't the point here).
  const peakPx = Math.max(...hours.map((h) => barHeightPx(h.gustMs * 1.944)), 1);
  const CHART_PX = 480;

  return (
    <div className={styles.canvas} ref={rootRef}>
      <CardHeader spot={spot} />
      <div className={styles.divider} />

      <div className={styles.hero}>
        <div className={styles.speedCol}>
          <div className={styles.speedRow}>
            <span className={styles.speed}>
              {heroHour ? fmtSpeed(heroHour.speedMs, unit) : "—"}
            </span>
            <span className={styles.unit}>{UNIT_LABELS[unit]}</span>
          </div>
          {heroHour && (
            <span className={styles.gust}>↑ {fmtSpeed(heroHour.gustMs, unit)} gusts</span>
          )}
        </div>
        {heroHour && (
          <div className={styles.dirCol}>
            <div className={styles.dirRow}>
              <DirArrow dirDeg={heroHour.dirDeg} color={accent} size={120} />
              <span className={styles.dir}>{dirLabel}</span>
            </div>
            <span className={styles.dirDeg}>{Math.round(heroHour.dirDeg)}°</span>
          </div>
        )}
      </div>

      <div className={styles.chart}>
        <div className={styles.bars}>
          {hours.map((h, i) => {
            const speedKn = h.speedMs * 1.944;
            const gustKn = h.gustMs * 1.944;
            const fillH = (barHeightPx(speedKn) / peakPx) * CHART_PX;
            const gustH = (barHeightPx(gustKn) / peakPx) * CHART_PX;
            const selected = i === hourIdx;
            const baseColor = speedKn < 3 ? "var(--wind-nil)" : knotsColor(speedKn);
            // Selected bar keeps its wind colour but darkened 60% (Figma:
            // a black overlay over the bar fill) so the locked hour reads
            // as set rather than just another bright bar.
            const fillBg = selected
              ? `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), ${baseColor}`
              : baseColor;
            return (
              <button
                type="button"
                key={h.time}
                className={styles.bar}
                onClick={interactive ? () => onSelectHour(i) : undefined}
              >
                {selected && (
                  <>
                    <div className={styles.chartLabel}>
                      {heroHour ? dayTimeLabel(heroHour.time) : ""}
                    </div>
                    <div className={styles.connector} style={{ bottom: Math.max(fillH, gustH) }} />
                  </>
                )}
                <div className={styles.barCol}>
                  {gustKn > speedKn + 0.5 && (
                    <div className={styles.gustFill} style={{ height: `${gustH}px` }} />
                  )}
                  <div
                    className={styles.fill}
                    style={{ height: `${fillH}px`, background: fillBg }}
                  />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Map canvas ─────────────────────────────────────────────────────────────

interface MapCanvasProps {
  rootRef: Ref<HTMLDivElement>;
  spot: EnrichedSpot;
  heroHour: ScoredHour | null;
  onMapReady: (map: MaplibreMap) => void;
}

function MapCanvas({ rootRef, spot, heroHour, onMapReady }: MapCanvasProps) {
  return (
    <div className={styles.canvas} ref={rootRef}>
      <CardHeader spot={spot} />
      <div className={styles.divider} />

      <div className={styles.mapFrame}>
        <SpotDetailMap
          coords={spot.coords}
          windDirectionDeg={heroHour?.dirDeg ?? null}
          windSpeedMs={heroHour?.speedMs ?? null}
          zoom={11}
          preserveDrawingBuffer
          onMapReady={onMapReady}
        />
        <div className={styles.compassOverlay}>
          <div className={styles.compassScale}>
            <CompassRing
              windDirectionDeg={heroHour?.dirDeg ?? null}
              windSpeedMs={heroHour?.speedMs ?? null}
              ideal={spot.ideal}
              secondary={spot.secondary}
            />
          </div>
        </div>
      </div>

      {heroHour && (
        <div className={styles.mapSummary}>
          <span>
            {Math.round(heroHour.speedMs * 1.944)} kn ·{" "}
            <span className={styles.mapGust}>{Math.round(heroHour.gustMs * 1.944)} gusts</span>
          </span>
          <span className={styles.mapSummaryRight}>
            {degreesToCompass(heroHour.dirDeg)} · {Math.round(heroHour.dirDeg)}°
          </span>
        </div>
      )}
    </div>
  );
}
